import React, { Component } from "react";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import { Tooltip, Icon, Badge } from "antd";

import {
  mobileDetect,
  tabletDetectByResolution,
} from "../../utils/mobileDetect";

import {
  openChat,
  closeChat,
  createChat,
  collapseChat,
  updateNewChat,
  updateChat,
  recaulculateMaxChats,
} from "../../store/actions/chats";

import { setCurrentUser, setUsers } from "../../store/actions/users";

import {
  togglePressEnterToSendMessage,
  toggleDynamicMessageFilter,
  toggleAlwaysOpenNewMessages,
  toggleReceiveEmailNotifications,
  toggleReceiveMentionEmailNotifications,
  setConfigurationId,
} from "../../store/actions/general-settings";

import { apiRefresh } from "../../store/actions/api";
import { toggleChatList } from "../../store/actions/chat-list";

import ChatList from "../ChatList";
import Navbar from "../Navbar";
import SearchBar from "../SearchBar";
import GeneralSettings from "../GeneralSettings";
import MessageBoxesWrapper from "../MessageBoxWrapper";

import UserService from "../../services/user";
import ChatService from "../../services/chat";
import FlagsService from "../../services/flags";
import CacheService from "../../services/cache";
import ConfigurationService from "../../services/configurations";

import { ReactComponent as MessageSVG } from "../../assets/icon/ic_message_24dp.svg";
import { ReactComponent as EmptyMessageSVG } from "../../assets/icon/ic_message_empty_24dp.svg";
import { ReactComponent as MessageDynamicSVG } from "../../assets/icon/ic_message_sync_24dp.svg";
import { ReactComponent as EmptyMessageDynamicSVG } from "../../assets/icon/ic_message_sync_empty_24dp.svg";
import { ReactComponent as FilterSVG } from "../../assets/icon/ic_list_filter_24dp.svg";
import { ReactComponent as FilterCloseSVG } from "../../assets/icon/ic_close_24dp.svg";
import { ReactComponent as WarningSVG } from "../../assets/icon/ic_warning_24dp.svg";

import "./MainContainer.scss";

let connection = null;

function ContentDisplay(props) {
  const {
    openMessageBox,
    collapsed,
    showMoreOptions,
    notifications,
    dismissNotification,
    chats,
    loadingChats,
    handleInfiniteOnLoad,
    hasMoreChats,
    hasErrors,
    isSearching,
    resetFilters,
    archiveChat,
    flags,
    mentions,
    reconnect,
  } = props;

  if (collapsed) {
    return false;
  }

  if (showMoreOptions) {
    return <GeneralSettings />;
  }

  return (
    <div className="main-content-wrapper">
      <ChatList
        list={chats}
        flags={flags}
        mentions={mentions}
        onCardClick={openMessageBox}
        notifications={notifications}
        dismissNotification={dismissNotification}
        loading={loadingChats}
        handleInfiniteOnLoad={handleInfiniteOnLoad}
        hasMoreChats={hasMoreChats}
        hasErrors={hasErrors}
        isSearching={isSearching}
        resetFilters={resetFilters}
        archiveChat={archiveChat}
        reconnect={reconnect}
      />
    </div>
  );
}

class MainContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      filter: 0,
      flags: [],
      currentFlag: null,
      showMoreOptions: false,
      connected: false,
      connectionClosed: false,
      connectionError: false,
      newMessages: {},
      notifications: {},
      mentions: {},
      profiles: [],
      templateSettings: {},
      messageVisibilityOptions: [],
      chats: [],
      chatsPage: 0,
      chatsError: false,
      totalChats: 0,
      hasMoreChats: false,
      loadingChats: false,
      isNewChatWaitingRealChat: false,
      isSearching: false,
      resetFilters: false,
      loadedConfigurations: false,
      navbarNotification: false,
      receivedNewMessages: false,
      totalNewMessages: 0,
      timeoutEffect: null,
    };
  }

  componentDidMount() {
    const chatConfig = window.Social.getChatConfig();
    UserService.getUser().then(
      (res) =>
        res &&
        res.data &&
        this.props.setCurrentUser(res.data) &&
        this.setupConfigVariables()
    );

    this.connectWS();

    this.getChatList();
    this.getTemplateSettings();
    this.getMessageVisibilityOptions();
    this.getUserConfiguration();
    this.getApplicationProfiles();
    this.setSocialConfig();

    chatConfig.allowFlags && this.getFlags();

    this.props.recaulculateMaxChats();

    window.onresize = this.props.recaulculateMaxChats;
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.flags.length !== prevState.flags.length) {
      this.setupConfigVariables();
    }

    if (this.props.refresh && !prevProps.refresh) {
      this.getChatList();
      this.props.apiRefresh(false);
    }

    if (prevState.chats !== this.state.chats) {
      const unreadMessages = this.state.chats.reduce(
        (acc, chat) => acc + chat.newMessages,
        0
      );

      this.setState({ totalNewMessages: unreadMessages });
    }

    if (prevState.totalNewMessages !== this.state.totalNewMessages) {
      if (this.state.timeoutEffect) {
        clearTimeout(this.state.timeoutEffect);
      }

      this.setState({ receivedNewMessages: true });

      const timeoutEffect = setTimeout(() => {
        this.setState({ receivedNewMessages: false });
      }, 1500);

      this.setState({ timeoutEffect });
    }
  }

  setSocialConfig = () => {
    const config = CacheService.getItem("SOCIAL_CONFIG");

    if (config && config.mainContainerOpened) {
      this.props.toggleChatList(config.mainContainerOpened);
    }
  };

  setupConfigVariables() {
    const taskFlag =
      this.state.flags &&
      this.state.flags.length &&
      this.state.flags.find((elem) => elem.value === "TASK");
    this.configVariables = {
      "%CURRENT_USER%": this.props.currentUser && this.props.currentUser.login,
      "%FLAG_TASK%": taskFlag.id,
    };
  }

  getFlags = () => {
    const configFlags = Object.values(window.Social.getChatFlags());
    FlagsService.getFlags().then((res) => {
      let flags = (res && res.data && res.data.content) || [];
      let mergedFlags = [];
      for (
        let flagIndex = 0, flagsLength = flags.length;
        flagIndex < flagsLength;
        flagIndex++
      ) {
        let currentFlag = flags[flagIndex];
        const configFlag = configFlags.find(
          (elem) => elem.name === currentFlag.flagName
        );
        mergedFlags.push({
          ...currentFlag,
          ...configFlag,
        });
      }
      this.setState(
        {
          flags: mergedFlags,
        },
        () => this.validateFlags(flags)
      );
    });
  };

  validateFlags = (flags) => {
    const configFlags = Object.values(window.Social.getChatFlags());
    for (let i = 0; i < configFlags.length; i++) {
      let configFlag = configFlags[i];
      let savedFlag = flags.find((elem) => elem.flagName === configFlag.name);
      if (!savedFlag) {
        this.saveFlag({
          flagName: configFlag.name,
          color: configFlag.color,
        });
      }
    }
  };

  saveFlag = (flag) => {
    FlagsService.postFlag(flag).then((res) => {
      res?.data?.content &&
        this.setState({
          flags: [].concat(this.state.flags, res.data.content),
        });
    });
  };

  getTemplateSettings = () => {
    ConfigurationService.getTemplateSettings().then((res) => {
      const { data } = res;

      this.setState({
        templateSettings: data,
      });
    });
  };

  getMessageVisibilityOptions = () => {
    ConfigurationService.getMessageVisibilityOptions().then((res) => {
      const { data } = res;

      this.setState({
        messageVisibilityOptions: data.visibilityOptions,
      });
    });
  };

  getApplicationProfiles = () => {
    ConfigurationService.getProfiles().then((res) => {
      res?.data?.content &&
        this.setState({
          profiles: Object.keys(res.data.content),
        });
    });
  };

  connectWS() {
    if (
      !(
        window.Social &&
        window.Social.getApiConfig() &&
        window.Social.getApiConfig().wsURL &&
        window.Social.getApiConfig().token
      )
    ) {
      return false;
    }

    connection = new WebSocket(
      window.Social.getApiConfig().wsURL + window.Social.getApiConfig().token
    );

    connection.onopen = () => {
      this.setState({
        connected: true,
        connectionClosed: false,
        connectionError: false,
      });
    };

    connection.onmessage = async (e) => {
      let data = JSON.parse(e.data);

      if (!data?.chat?.id) {
        return;
      }

      const chatConfig = window.Social.getChatConfig();
      const itsMe = this.isFromCurrentUser(data.from.username);
      const openedChat = this.isChatOpen(data.chat.id);

      if (this.state.isSearching && this.searchBarGetChats) {
        this.searchBarGetChats();
      }

      if (this.isChatListed(data.chat.id)) {
        let chats = this.removeReactivity(this.state.chats);
        let chatIndex = chats.findIndex((elem) => elem.id === data.chat.id);
        let currentChat;

        if (chatIndex > -1) {
          currentChat = chats[chatIndex];
          chats.splice(chatIndex, 1);
        }

        data.chat.newMessages = itsMe ? 0 : (currentChat.newMessages || 0) + 1;

        chats.unshift(data.chat);

        this.setState({
          chats: chats,
        });
      } else {
        this.state.isNewChatWaitingRealChat &&
          this.props.updateNewChat(data.chat);

        if (!this.state.isSearching) {
          let newChats = this.removeReactivity(this.state.chats);
          const newMessages = itsMe ? 0 : 1;

          newChats.length === chatConfig.chatsPageSize && newChats.pop();
          newChats.unshift({ ...data.chat, newMessages });

          this.setState({
            chats: newChats,
            isNewChatWaitingRealChat: false,
            totalChats: this.state.totalChats + 1,
          });
        }
      }

      if (openedChat) {
        let newMessages = this.removeReactivity(this.state.newMessages);

        newMessages[data.chat.id] = data;

        this.setState({
          newMessages: newMessages,
        });

        openedChat.archived &&
          this.props.updateChat({ id: data.chat.id, archived: false });
      } else if (this.props.alwaysOpenNewMessages) {
        this.props.openChat(data.chat);
      }

      if (!itsMe) {
        let mentions = this.removeReactivity(this.state.mentions);
        let notifications = this.removeReactivity(this.state.notifications);

        notifications[data.chat.id] = data;

        if (
          data.mentions &&
          data.mentions.length &&
          data.mentions.includes(this.props.currentUser.login)
        ) {
          mentions[data.chat.id] = mentions[data.chat.id]
            ? mentions[data.chat.id] + 1
            : 1;
        }

        this.setState({
          notifications: notifications,
          mentions: mentions,
          navbarNotification: true,
        });
      }
    };

    connection.onerror = (err) => {
      this.setState(
        {
          connectionError: true,
          connectionClosed: true,
        },
        connection.close()
      );
    };

    connection.onclose = (e) => {
      this.setState({
        connected: false,
        connectionClosed: true,
      });

      setTimeout(() => {
        this.connectWS();
      }, 5000);
    };
  }

  getSearchBarGetChatsFunction(func) {
    this.searchBarGetChats = func;
  }

  getUserConfiguration() {
    UserService.getUserConfiguration()
      .then(this.setUserConfiguration.bind(this))
      .catch(this.postUserConfiguration.bind(this));
  }

  setUserConfiguration(data) {
    data.pressEnterToSendMessages && this.props.togglePressEnterToSendMessage();
    data.dynamicMessageFilter && this.props.toggleDynamicMessageFilter();
    data.alwaysOpenNewMessages && this.props.toggleAlwaysOpenNewMessages();
    data.alwaysNotifyByEmail && this.props.toggleReceiveEmailNotifications();
    data.notifyOnMentionOnly &&
      this.props.toggleReceiveMentionEmailNotifications();
    data.id && this.props.setConfigurationId(data.id);

    this.setState({
      loadedConfigurations: true,
    });
  }

  postUserConfiguration() {
    UserService.postUserConfiguration({
      alwaysOpenNewMessages: false,
      dynamicMessageFilter: false,
      alwaysNotifyByEmail: false,
      filterMessageStatus: "ACTIVE",
      pressEnterToSendMessages: false,
    }).then(this.setUserConfiguration.bind(this));
  }

  isFilteringByArchived() {
    return this.state.filter === 1;
  }

  getChatList(params = {}) {
    let isSearching = Object.values(params).length > 0;
    let filter = window.Social.getFilterConfig()[this.state.filter];
    let filterParams = {};

    for (let i in filter.params) {
      let param = filter.params[i];

      if (/%.*%/.test(param)) {
        filterParams[i] = this.configVariables[param];
      } else {
        filterParams[i] = param;
      }
    }

    if (filter) {
      params = {
        ...params,
        ...filterParams,
      };

      isSearching = isSearching || filter.refreshOnNewMessages;
    }

    this.setState({
      loadingChats: true,
      isSearching: isSearching,
    });

    ChatService.getChats(params)
      .then((res) => {
        let mentions = {};
        let notifications = {};

        for (let i = 0; i < res.data.content.length; i++) {
          let chat = res.data.content[i];
          if (chat.mentions) {
            mentions[chat.id] = chat.mentions;
          }

          if (chat.newMessages) {
            notifications[chat.id] = chat;
          }
        }

        const navbarNotification =
          Object.keys(notifications).length || Object.keys(mentions).length;

        if (res?.data?.content) {
          this.setState({
            chats: res.data.content,
            loadingChats: false,
            totalChats: res.data.totalElements,
            chatsPage: 0,
            hasMoreChats: !res.data.last,
            chatsError: false,
            mentions,
            notifications,
            navbarNotification,
          });
        }
      })
      .catch((err) => {
        this.setState({
          loadingChats: false,
          hasMoreChats: false,
          chatsError: true,
        });
      });
  }

  handleInfiniteOnLoad() {
    if (!this.state.loadingChats) {
      let params = {};

      this.setState({
        loadingChats: true,
      });

      if (this.state.filter === 0) {
        params.archived = false;
      } else if (this.state.filter === 1) {
        params.archived = true;
      }

      params.page = this.state.chatsPage + 1;

      ChatService.getChats(params).then((res) => {
        if (res?.data?.content) {
          this.setState({
            chats: this.state.chats.concat(res.data.content),
            loadingChats: false,
            totalChats: res.data.totalElements,
            chatsPage: this.state.chatsPage + 1,
            hasMoreChats: !res.data.last,
          });
        }
      });
    }
  }

  isChatOpen(chatId) {
    return this.removeReactivity(this.props.globalChats).find(
      (elem) => elem.id === chatId
    );
  }

  isFromCurrentUser(username) {
    return username === this.props.currentUser.login;
  }

  isChatListed(chatId) {
    return this.removeReactivity(this.state.chats).find(
      (elem) => elem.id === chatId
    );
  }

  handshakeNewChatCreated() {
    this.setState({
      isNewChatWaitingRealChat: true,
    });
  }

  getChatNewMessages(chatId) {
    return this.state.newMessages[chatId];
  }

  getChatById(chatId) {
    return this.props.globalChats.find((chat) => chat.id === chatId);
  }

  onCardClick(chatId) {
    const chat = this.getChatById(chatId);
    const isTablet = mobileDetect() || tabletDetectByResolution();
    const canOpenChat = isTablet && chat && chat.collapsed;

    if (!isTablet || canOpenChat) {
      this.props.collapseChat(chatId);
    }
  }

  openMessageBox(chat, focusOnOpen = false) {
    if (!chat.newChat) {
      chat && chat.id && this.dismissNotification(chat.id);
      ChatService.viewChat(chat.id);
    }

    if (focusOnOpen) {
      chat.focusOnOpen = focusOnOpen;
    }

    this.props.openChat(chat);
  }

  closeChat(chatId) {
    this.props.closeChat(chatId);
  }

  removeReactivity(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  onNavbarClick() {
    CacheService.saveItem("SOCIAL_CONFIG", {
      mainContainerOpened: !this.props.chatsListOpened,
    });

    this.props.toggleChatList(!this.props.chatsListOpened);
  }

  setChatAsRead(chatId) {
    let chats = this.removeReactivity(this.state.chats);
    let chatIndex = chats.findIndex((elem) => elem.id === chatId);

    if (chatIndex > -1 && chats[chatIndex].newMessages) {
      chats[chatIndex].newMessages = 0;

      this.setState({
        chats: chats,
      });
    }
  }

  updateChatState(chatId, updatedChat) {
    let chats = this.removeReactivity(this.state.chats);
    let chatIndex = chats.findIndex((elem) => elem.id === chatId);

    if (chatIndex > -1)
      chats[chatIndex] = {
        ...chats[chatIndex],
        ...updatedChat,
      };

    this.setState({
      chats: chats,
    });
  }

  archiveChat(e, chat) {
    e.stopPropagation();

    ChatService.archiveChat(chat.id).then(this.searchBarGetChats);

    this.props.updateChat({ id: chat.id, archived: true });
  }

  getNavbarStatus() {
    const { formatMessage } = this.props.intl;
    const themeConfig = window.Social.getThemeConfig();

    if (
      this.state.showMoreOptions ||
      (themeConfig && themeConfig.minimalist && this.props.chatsListOpened)
    ) {
      return false;
    }

    let filter = window.Social.getFilterConfig()[this.state.filter];
    let prefix = this.state.totalChats
      ? this.state.totalChats
      : formatMessage({ id: "filter.noMessages" });

    return formatMessage(
      { id: "filter.numberOfactiveMessages" },
      {
        number: prefix,
        sufix:
          this.state.totalChats > 1
            ? formatMessage({ id: filter.sufixForLabels })
            : formatMessage({ id: filter.sufixForLabels + ".singular" }),
      }
    );
  }

  setFilter(filter) {
    let callback = this.searchBarGetChats || this.getChatList;

    this.setState(
      {
        filter: filter,
      },
      callback
    );
  }

  toggleMoreOptions(e) {
    e && e.stopPropagation();

    this.setState({
      showMoreOptions: !this.state.showMoreOptions,
    });
  }

  getNavBarContent() {
    const themeConfig = window.Social.getThemeConfig();

    if (!themeConfig || (themeConfig && !themeConfig.minimalist)) {
      return null;
    }

    return (
      <span className="minimalist-title">
        {this.state.loadingChats && (
          <span className="loading">
            <Icon type="loading" />
          </span>
        )}

        <Badge
          count={this.state.totalNewMessages}
          overflowCount={9}
          className={`total-unread-messages ${
            this.state.receivedNewMessages ? "pulse" : ""
          }`}
        >
          {this.getMinimalistIcon()}
        </Badge>
      </span>
    );
  }

  getNavbarTitle() {
    const { formatMessage } = this.props.intl;

    if (this.props.chatsListOpened) {
      return this.getNavBarContent();
    }

    return this.state.showMoreOptions
      ? formatMessage({ id: "chat.settings" })
      : formatMessage({ id: "chat.messages" });
  }

  resetFilters(e) {
    e.stopPropagation();
    this.setState({ resetFilters: true });
  }

  hasConnectionErrors() {
    return (
      this.state.connectionClosed ||
      this.state.chatsError ||
      this.state.connectionClosed ||
      this.state.connectionError
    );
  }

  getMinimalistIcon() {
    const { formatMessage } = this.props.intl;

    if (this.hasConnectionErrors()) {
      return (
        <Tooltip
          placement="topRight"
          overlayClassName="social-tooltip"
          title={this.getMinimalistTooltip()}
          overlayStyle={{ maxWidth: "300px" }}
        >
          <div className="minimalist-actions connection-error one-icon">
            <EmptyMessageSVG />
          </div>
        </Tooltip>
      );
    }

    const isEmpty = this.state.totalChats === 0;

    if (this.state.isSearching && this.props.dynamicMessageFilter) {
      return (
        <div className="minimalist-actions searching-mode">
          <Tooltip
            placement="topRight"
            overlayClassName="social-tooltip"
            title={this.getMinimalistTooltip()}
            overlayStyle={{ maxWidth: "300px" }}
          >
            {isEmpty ? <EmptyMessageDynamicSVG /> : <MessageDynamicSVG />}
          </Tooltip>

          <Tooltip
            title={formatMessage({ id: "filter.clearFilters" })}
            placement="topRight"
            overlayClassName="social-tooltip"
          >
            <FilterSVG
              className="reset-filter"
              onClick={this.resetFilters.bind(this)}
            />

            <FilterCloseSVG
              className="reset-filter-close"
              onClick={this.resetFilters.bind(this)}
            />
          </Tooltip>
        </div>
      );
    }

    if (this.state.isSearching && !this.props.dynamicMessageFilter) {
      return (
        <div className="minimalist-actions searching-mode">
          <Tooltip
            overlayClassName="social-tooltip"
            placement="topRight"
            title={this.getMinimalistTooltip()}
            overlayStyle={{ maxWidth: "300px" }}
          >
            {isEmpty ? <EmptyMessageSVG /> : <MessageSVG />}
          </Tooltip>

          <Tooltip
            title={formatMessage({ id: "filter.clearFilters" })}
            overlayClassName="social-tooltip"
            placement="topRight"
          >
            <FilterSVG
              className="reset-filter"
              onClick={this.resetFilters.bind(this)}
            />

            <FilterCloseSVG
              className="reset-filter-close"
              onClick={this.resetFilters.bind(this)}
            />
          </Tooltip>
        </div>
      );
    }

    if (!this.state.isSearching && !this.props.dynamicMessageFilter) {
      return (
        <Tooltip
          overlayClassName="social-tooltip"
          placement="topRight"
          title={this.getMinimalistTooltip()}
          overlayStyle={{ maxWidth: "300px" }}
        >
          <div className="minimalist-actions one-icon">
            {isEmpty ? <EmptyMessageSVG /> : <MessageSVG />}
          </div>
        </Tooltip>
      );
    }

    if (!this.state.isSearching && this.props.dynamicMessageFilter) {
      return (
        <Tooltip
          overlayClassName="social-tooltip"
          placement="topRight"
          title={this.getMinimalistTooltip()}
          overlayStyle={{ maxWidth: "300px" }}
        >
          <div className="minimalist-actions one-icon">
            {isEmpty ? <EmptyMessageDynamicSVG /> : <MessageDynamicSVG />}
          </div>
        </Tooltip>
      );
    }
  }

  getMinimalistTooltip() {
    const { formatMessage } = this.props.intl;

    if (this.hasConnectionErrors()) {
      return formatMessage({ id: "chat.messages" });
    }

    let filter = window.Social.getFilterConfig()[this.state.filter];
    let contextFilter = this.props.contextFilter.find(
      (elem) =>
        elem.description === window.Social.getChatConfig().hashElementPrefix
    );
    contextFilter = contextFilter
      ? contextFilter && contextFilter.content
      : null;

    let prefix = this.state.totalChats
      ? this.state.totalChats
      : formatMessage({ id: "filter.noMessages" });

    return `${contextFilter ? "#" + contextFilter + " - " : ""} ${formatMessage(
      { id: "filter.numberOfactiveMessages" },
      {
        number: prefix,
        sufix:
          this.state.totalChats > 1
            ? formatMessage({ id: filter.sufixForLabels })
            : formatMessage({ id: filter.sufixForLabels + ".singular" }),
      }
    )}`;
  }

  async dismissNotification(chatId) {
    if (this.state.notifications[chatId]) {
      let notifications = this.removeReactivity(this.state.notifications);

      delete notifications[chatId];

      let mentions = this.removeReactivity(this.state.mentions);

      delete mentions[chatId];

      this.setState({
        notifications: notifications,
        mentions: mentions,
        navbarNotification: false,
      });

      await ChatService.viewChat(chatId);
    }

    this.setChatAsRead(chatId);
  }

  setCurrentFlag = (flagId) => {
    let callback = this.searchBarGetChats || this.getChatList;

    this.setState(
      {
        currentFlag: flagId,
      },
      callback
    );
  };

  render() {
    const themeConfig = window.Social.getThemeConfig();
    const minimalistTheme =
      themeConfig && themeConfig.minimalist ? "minimalist" : "";
    const socialButtonTheme = themeConfig && themeConfig.socialButton;
    const socialFloatButtonTheme = themeConfig && themeConfig.socialFloatButton;

    return (
      <div className="social-container">
        {socialButtonTheme && (
          <div
            className={`navbar-button`}
            onClick={this.onNavbarClick.bind(this)}
          >
            {this.getNavBarContent()}
          </div>
        )}

        <div className="main-container">
          <div
            className={`main-content ${
              this.props.chatsListOpened ? "collapsed" : ""
            } ${minimalistTheme}`}
            onClick={() => this.setState({ navbarNotification: false })}
          >
            {(socialFloatButtonTheme || !this.props.chatsListOpened) && (
              <Navbar
                title={this.getNavbarTitle()}
                status={this.getNavbarStatus()}
                openMessageBox={this.props.createChat}
                parentCollapsed={this.props.chatsListOpened}
                settingsOpen={this.state.showMoreOptions}
                toggleMoreOptions={this.toggleMoreOptions.bind(this)}
                loadedConfigurations={this.state.loadedConfigurations}
                notify={this.state.navbarNotification}
                onNavbarClick={this.onNavbarClick.bind(this)}
              />
            )}

            <SearchBar
              setFilter={this.setFilter.bind(this)}
              selectedFilterId={this.state.filter}
              getChatList={this.getChatList.bind(this)}
              canResetFilters={this.state.resetFilters}
              filtersReseted={() => this.setState({ resetFilters: false })}
              getSearchBarGetChatsFunction={this.getSearchBarGetChatsFunction.bind(
                this
              )}
              flags={this.state.flags}
              setCurrentFlag={this.setCurrentFlag.bind(this)}
              collapsed={
                this.props.chatsListOpened || this.state.showMoreOptions
              }
            />

            <ContentDisplay
              collapsed={this.props.chatsListOpened}
              openMessageBox={this.openMessageBox.bind(this)}
              flags={this.state.flags}
              mentions={this.state.mentions}
              showMoreOptions={this.state.showMoreOptions}
              notifications={this.state.notifications}
              dismissNotification={this.dismissNotification.bind(this)}
              chats={this.state.chats}
              loadingChats={this.state.loadingChats}
              handleInfiniteOnLoad={this.handleInfiniteOnLoad.bind(this)}
              hasMoreChats={this.state.hasMoreChats}
              hasErrors={this.hasConnectionErrors()}
              isSearching={this.state.isSearching}
              archiveChat={this.archiveChat.bind(this)}
              setCurrentFlag={this.setCurrentFlag.bind(this)}
              resetFilters={() => this.setState({ resetFilters: true })}
              reconnect={() => this.connectWS()}
            />
          </div>

          <div className={"message-boxes-container right"}>
            <MessageBoxesWrapper
              chats={this.props.globalChats}
              templateSettings={this.state.templateSettings}
              messageVisibilityOptions={this.state.messageVisibilityOptions}
              hiddenChats={this.props.globalHidden}
              closeChat={this.closeChat.bind(this)}
              onCardClick={this.onCardClick.bind(this)}
              openMessageBox={this.openMessageBox.bind(this)}
              getChatNewMessages={this.getChatNewMessages.bind(this)}
              dismissNotification={this.dismissNotification.bind(this)}
              handshakeNewChatCreated={this.handshakeNewChatCreated.bind(this)}
              setChatAsRead={this.setChatAsRead.bind(this)}
              notifications={this.state.notifications}
              mentions={this.state.mentions}
              profiles={this.state.profiles}
              wsConnection={connection}
              updateChatState={this.updateChatState.bind(this)}
              getChatList={this.searchBarGetChats}
              flags={this.state.flags}
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ ChatList, Chats, GeneralSettings, Users, Api }) => {
  return {
    chatsListOpened: ChatList.chatsListOpened,
    globalChats: Chats.chats,
    globalHidden: Chats.hidden,
    currentUser: Users.currentUser,
    alwaysOpenNewMessages: GeneralSettings.alwaysOpenNewMessages,
    dynamicMessageFilter: GeneralSettings.dynamicMessageFilter,
    refresh: Api.refresh,
    contextFilter: Api.contextFilter,
  };
};

const mapDispatchToProps = (dispatch) => ({
  toggleChatList: (status) => dispatch(toggleChatList(status)),
  openChat: (chat) => dispatch(openChat(chat)),
  closeChat: (chat) => dispatch(closeChat(chat)),
  createChat: (chat) => dispatch(createChat(chat)),
  collapseChat: (id) => dispatch(collapseChat(id)),
  updateNewChat: (chat) => dispatch(updateNewChat(chat)),
  updateChat: (chat) => dispatch(updateChat(chat)),
  recaulculateMaxChats: () => dispatch(recaulculateMaxChats()),
  setCurrentUser: (user) => dispatch(setCurrentUser(user)),
  setUsers: (users) => dispatch(setUsers(users)),
  toggleDynamicMessageFilter: () => dispatch(toggleDynamicMessageFilter()),
  toggleAlwaysOpenNewMessages: () => dispatch(toggleAlwaysOpenNewMessages()),
  togglePressEnterToSendMessage: () =>
    dispatch(togglePressEnterToSendMessage()),
  toggleReceiveEmailNotifications: () =>
    dispatch(toggleReceiveEmailNotifications()),
  toggleReceiveMentionEmailNotifications: () =>
    dispatch(toggleReceiveMentionEmailNotifications()),
  setConfigurationId: (id) => dispatch(setConfigurationId(id)),
  apiRefresh: (status) => dispatch(apiRefresh(status)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(MainContainer));
